import {
  ArrowUpIcon,
  BadgePercentIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  EarthIcon,
  HouseIcon,
  LucideIcon,
  PieChartIcon,
  RefreshCwIcon,
  ShoppingCartIcon,
  UserIcon,
} from "lucide-react";
import { cn } from "web-design-system";
import { BrandLogo } from "./brand-logo";
import { PhoneFrame } from "./phone-frame";
import { BrandingPreviewValues } from "./types";

// Dummy portfolio, colours mirror the investor app (ASSET_ALLOCATION_BUCKET_COLORS)
const SUMMARY = {
  currentValue: "₹10,28,630",
  returnPct: "37.3%",
  invested: "₹7,49,060",
  totalReturn: "₹2,79,570",
};

const ALLOCATION = [
  { key: "indian_equity", pct: 86, color: "#024B7B" },
  { key: "debt", pct: 8, color: "#45B7C2" },
  { key: "commodity", pct: 6, color: "#FFAF4A" },
];

const PRODUCTS: {
  label: string;
  subtitle: string;
  value: string;
  growth?: string;
  icon: LucideIcon;
}[] = [
  {
    label: "Indian Mutual Funds",
    subtitle: "100.0% allocation",
    value: "₹10.2L",
    growth: "37.3%",
    icon: PieChartIcon,
  },
  {
    label: "SIFs",
    subtitle: "High ticket investments",
    value: "₹0.0",
    icon: BadgePercentIcon,
  },
  {
    label: "GIFT Outbound",
    subtitle: "Global exposure",
    value: "₹0.0",
    icon: EarthIcon,
  },
];

const TABS = [
  { label: "Home", icon: HouseIcon, active: true },
  { label: "Investments", icon: PieChartIcon },
  { label: "Account", icon: UserIcon },
];

export function MobileHomePreview({
  brandName,
  tagLine,
  logoUrl,
}: BrandingPreviewValues) {
  return (
    <PhoneFrame footer={<TabBar />}>
      <div className="flex flex-col px-4">
        <div className="flex items-center gap-3 py-4">
          <BrandLogo
            url={logoUrl}
            name={brandName}
            className="size-12 rounded-xl"
          />
          <div className="flex flex-col min-w-0">
            <span className="font-semibold truncate">{brandName}</span>
            <span className="text-xs text-stone-500 truncate">{tagLine}</span>
          </div>
        </div>

        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-2 text-sm font-semibold">
            Hi, John Doe
            <ChevronDownIcon className="size-3.5" />
          </div>
          <div className="relative flex items-center justify-center size-10 rounded-lg border border-stone-200 bg-white">
            <span className="absolute -top-0.5 -right-1 size-2.5 rounded-full bg-red-500" />
            <ShoppingCartIcon className="size-5" />
          </div>
        </div>

        <PortfolioSummary />
        <ProductsSection />
      </div>
    </PhoneFrame>
  );
}

function PortfolioSummary() {
  return (
    <div className="flex flex-col gap-6 rounded-xl bg-[radial-gradient(circle_at_50%_0%,#D4EAFF,#E6F6F9)] p-3">
      <div className="grid grid-cols-3 rounded-lg bg-white/40 p-1 text-xs">
        {["All", "Internal", "External"].map((tab, index) => (
          <span
            key={tab}
            className={cn(
              "py-1.5 text-center rounded-md",
              index === 0 ? "bg-white font-medium" : "text-stone-600"
            )}
          >
            {tab}
          </span>
        ))}
      </div>
      <div className="flex flex-col gap-6 px-2">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-stone-500">Your wealth today</span>
            <div className="flex items-center gap-2">
              <span className="text-xl font-semibold">
                {SUMMARY.currentValue}
              </span>
              <span className="flex items-center text-sm font-medium text-green-600">
                <ArrowUpIcon className="size-3.5" />
                {SUMMARY.returnPct}
              </span>
            </div>
          </div>
          <div className="flex items-center justify-center size-8 rounded-lg bg-white/60">
            <RefreshCwIcon className="size-4" />
          </div>
        </div>
        <div className="flex justify-between">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-stone-500">Invested</span>
            <span className="text-sm font-semibold">{SUMMARY.invested}</span>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className="text-xs text-stone-500">Total Return</span>
            <span className="text-sm font-semibold">{SUMMARY.totalReturn}</span>
          </div>
        </div>
        <div className="flex flex-col gap-3 pb-3">
          <div className="flex items-center justify-between text-xs font-medium">
            <span className="text-stone-500">Asset Allocation</span>
            <span className="flex items-center gap-0.5">
              View details
              <ChevronRightIcon className="size-3.5" />
            </span>
          </div>
          <div className="flex h-1.5 gap-1">
            {ALLOCATION.map((bucket) => (
              <span
                key={bucket.key}
                className="rounded-full"
                style={{ width: `${bucket.pct}%`, background: bucket.color }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProductsSection() {
  return (
    <div className="flex flex-col gap-4 py-6">
      <span className="text-xs font-medium uppercase text-stone-500">
        Products
      </span>
      {PRODUCTS.map(({ label, subtitle, value, growth, icon: Icon }) => (
        <div key={label} className="flex items-center gap-3">
          <div className="flex items-center justify-center size-9 rounded-lg bg-[#EDF4FB]">
            <Icon className="size-4 text-[#25385C]" strokeWidth={1.5} />
          </div>
          <div className="flex flex-col flex-1 min-w-0">
            <span className="text-xs font-semibold">{label}</span>
            <span className="text-[10px] text-stone-500">{subtitle}</span>
          </div>
          <div className="flex flex-col items-end">
            <span className="text-xs font-semibold">{value}</span>
            {growth && (
              <span className="flex items-center text-[10px] text-green-600">
                <ArrowUpIcon className="size-2.5" />
                {growth}
              </span>
            )}
          </div>
          <ChevronRightIcon className="size-4 text-stone-700" />
        </div>
      ))}
    </div>
  );
}

function TabBar() {
  return (
    <div className="grid grid-cols-3 gap-2 border-t border-stone-200 bg-white p-2">
      {TABS.map(({ label, icon: Icon, active }) => (
        <div
          key={label}
          className={cn(
            "flex flex-col items-center gap-1 rounded-xl py-2 text-[10px]",
            active ? "bg-[#EDF4FB] font-medium" : "text-stone-500"
          )}
        >
          <Icon className={cn("size-4", active && "fill-current")} />
          {label}
        </div>
      ))}
    </div>
  );
}
